import React from "react";
import {
  IconCopy,
  IconEdit,
  IconInfoCircle,
  IconTrash,
} from "@tabler/icons-react";
import {
  InternalProductSearchOutput,
  ProductSourceEnum,
  ProductType,
} from "api-client";
import clsx from "clsx";
import Tooltip from "components/Tooltip";
import { useTranslation } from "react-i18next";
import { searchStore } from "features/ProductSearch/SearchStore";
import { buildingStore } from "store/Building/BuildingStore";
import { COEStore } from "features/ComponentEditor/ComponentEditorStore";
import { useNavigate } from "react-router";
import useProductActions from "../useProductActions";
import { ROUTES_ENUM } from "routes/consts";
import { productSearchStore as store } from "../ProductSearchStore";
import { ProductActionMode } from "utils";
import { sidebarMenuStore } from "features/SidebarMenu/SidebarMenuStore";

const RowHoverActions = ({
  product,
  type,
}: {
  product: InternalProductSearchOutput;
  type?: string;
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { saveStateAndGetPath, editComponent, duplicateComponent } =
    useProductActions();

  const duplicateProduct = (id?: string | null) => {
    const pathname = saveStateAndGetPath();
    navigate(`${pathname}/${ROUTES_ENUM.PRODUCT_EDITOR}`, {
      state: { id: id, compliant: false, mode: ProductActionMode.Duplicate },
    });
  };

  const editProduct = (id?: string | null) => {
    const pathname = saveStateAndGetPath();
    navigate(`${pathname}/${ROUTES_ENUM.PRODUCT_EDITOR}`, {
      state: { id: id, compliant: false, mode: ProductActionMode.Edit },
    });
  };

  const usedProductsHoverItems = [
    {
      name: t("mapping.showDetails"),
      icon: <IconInfoCircle width={24} className="mr-2" />,
      onClick: () =>
        searchStore.setOpenDetails({ open: true, product: product }),
      className: "text-gray-700",
      disabled: false,
    },
  ];

  const isProduct = product.type === ProductType.Product;

  const isManufacturerSource =
    product.dataset_source?.toLowerCase() ===
    ProductSourceEnum.Manufacturer.toLowerCase();

  // The edit button is only shown for products that belong to the user's
  // organisation (e.g. in the "All" tab, Concular catalog rows have no edit
  // button). Whether it is then clickable stays bound to `can_edit` below.
  const isOwnedByOrg =
    !!product.organisation_id &&
    product.organisation_id === sidebarMenuStore.currentOrganisation?.id;

  function editProductTitle() {
    let title: string;
    if (isManufacturerSource && product.permissions?.can_edit === false) {
      title = t("productSearch.editDisableManufacturer");
    } else if (product.permissions?.can_edit === false) {
      title = t("productSearch.editNoPermission");
    } else {
      title = t("mapping.editProduct");
    }

    return <div className="flex justify-between w-full">{title}</div>;
  }

  function duplicateProductTitle() {
    return isManufacturerSource && product.permissions?.can_duplicate === false
      ? t("productSearch.duplicateDisabledManufacturer")
      : t(
          `mapping.${
            product?.type === "PRODUCT"
              ? "duplicateProduct"
              : "duplicateComponent"
          }`
        );
  }

  const allProductsHoverItems = [
    {
      name: editProductTitle(),
      icon: <IconEdit width={24} className="mr-2" stroke={2} />,
      onClick: () =>
        isProduct ? editProduct(product?.id) : editComponent(product?.id),
      className: "text-indigo-700",
      disabled:
        (COEStore.openedSearchInCE && product.type === ProductType.Component) ||
        buildingStore.calculationsStatus.inProgress ||
        product.permissions?.can_edit === false,
      hidden: !isOwnedByOrg,
    },
    {
      name: duplicateProductTitle(),
      icon: <IconCopy width={24} className="mr-2" stroke={2} />,
      onClick: () =>
        isProduct
          ? duplicateProduct(product?.id)
          : duplicateComponent(product?.id),
      className: "text-indigo-700",
      disabled:
        (COEStore.openedSearchInCE && product.type === ProductType.Component) ||
        (product.permissions?.can_duplicate === false && isManufacturerSource),
    },
    {
      name: t("mapping.showDetails"),
      icon: <IconInfoCircle width={24} className="mr-2" stroke={2} />,
      onClick: () =>
        searchStore.setOpenDetails({ open: true, product: product }),
      className: "text-indigo-700",
      disabled: false,
    },
    {
      name: t(`mapping.${isProduct ? "removeProduct" : "removeComponent"}`),
      icon: <IconTrash width={24} stroke={2} className="mr-2" />,
      onClick: () => {
        if (product?.is_part_of_a_component) {
          store.setRemoveItemState({
            open: true,
            id: null,
            name: null,
            type: product?.type,
          });
        } else {
          store.setRemoveItemState({
            open: true,
            id: product?.id,
            name: product?.name,
            type: product?.type,
          });
        }
      },
      className: "text-red-700",
      disabled:
        buildingStore.calculationsStatus.inProgress ||
        COEStore.openedSearchInCE,
      hidden: product.permissions?.can_delete === false,
    },
  ];

  const items =
    type === "usedProducts"
      ? usedProductsHoverItems
      : allProductsHoverItems.filter((rec) => !rec.hidden);

  if (!items) return;
  return (
    <div className="flex items-center ml-3">
      {items.map((rec, index) => (
        <Tooltip
          key={index}
          content={rec.name}
          hoverOnTooltip
          className={clsx(
            "flex items-center w-full py-2 cursor-pointer text-indigo-700",
            rec.className
          )}
        >
          <div
            className={
              rec.disabled ? "opacity-50 cursor-not-allowed" : "opacity-100"
            }
            onClick={() => !rec.disabled && rec.onClick()}
          >
            {rec.icon}
          </div>
        </Tooltip>
      ))}
    </div>
  );
};

export default RowHoverActions;
